/**
 * useMultizonePlayback — central hook for multi-zone grouped playback.
 *
 * iOS path:  delegates to ClientMultizoneHandler → NativeMultizoneHandler
 * Connector: manages state locally, fires individual Tauri IPC commands per zone
 * Browser:   multizone not available (single audio output)
 *
 * Volume model (Paul's design spec):
 *   masterVolume = average of all zone actualVolumes
 *   Moving master: ratio = newMaster / oldMaster, each zone *= ratio
 *   Moving per-zone: set zone directly, master = new average
 *   Edge case: all zones at 0, master increase sets all zones to newMaster value
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMaestroStore } from '../store/maestroStore';
import { getClientMultizoneHandler } from '../handlers/ClientMultizoneHandler';
import { isConnectorAvailable } from '../lib/connector';
import * as Connector from '../lib/connector';
import { TransportCommand } from '../transport/TransportCommand';
import { QueueController } from '../transport/QueueController';
import { detectPlatform } from '../utils/platform';

const VOLUME_DEBOUNCE_MS = 150;

const isNativeiOS = () => detectPlatform().isIOSNativeApp;

export default function useMultizonePlayback() {
  const multizone = useMaestroStore((s) => s.multizone);
  const setMultizoneState = useMaestroStore((s) => s.setMultizoneState);
  const setMultizoneEnabled = useMaestroStore((s) => s.setMultizoneEnabled);
  const setGroupedZoneActualVolume = useMaestroStore((s) => s.setGroupedZoneActualVolume);
  const primaryZoneRef = useRef(null);

  const masterDebounceRef = useRef(null);
  const zoneDebounceRefs = useRef({});

  const useNative = isNativeiOS();

  // --- Enable / Disable with transition logic ---

  const enableMultizone = useCallback(async () => {
    const store = useMaestroStore.getState();
    primaryZoneRef.current = store.activeConnectorRenderer
      ? { id: store.activeConnectorRenderer.id, name: store.activeConnectorRenderer.name }
      : null;

    console.log('[enableMultizone] BEFORE: multizone state:', JSON.stringify(store.multizone));

    if (useNative) {
      getClientMultizoneHandler().enable();
      return;
    }

    setMultizoneEnabled(true);

    const afterEnable = useMaestroStore.getState().multizone;
    console.log('[enableMultizone] AFTER setEnabled: enabled:', afterEnable.enabled,
      'groupedZones:', afterEnable.groupedZones.length,
      'zones:', afterEnable.groupedZones.map(z => z.zoneName));

    // If music is currently playing, pause → re-resolve at CD quality → send to all
    if (store.playbackState === 'playing' && store.currentTrack) {
      await TransportCommand.pause(true);
      // Wait briefly for pause to settle
      await new Promise(r => setTimeout(r, 300));
      const mz = useMaestroStore.getState().multizone;
      console.log('[enableMultizone] Re-check after pause: groupedZones:', mz.groupedZones.length);
      if (mz.groupedZones.length >= 2) {
        const queueIdx = store.queueIndex;
        await QueueController.playQueueTrack(queueIdx);
      }
    }
  }, [useNative, setMultizoneEnabled]);

  const disableMultizone = useCallback(async () => {
    if (useNative) {
      getClientMultizoneHandler().disable();
      return;
    }

    const store = useMaestroStore.getState();
    const wasPlaying = store.playbackState === 'playing';
    const mz = store.multizone;
    const primary = primaryZoneRef.current;

    // Stop all zones except the primary
    if (wasPlaying && isConnectorAvailable() && mz.groupedZones.length >= 2) {
      const zonesToStop = primary
        ? mz.groupedZones.filter(z => z.zoneId !== primary.id)
        : mz.groupedZones.slice(1);
      await Promise.allSettled(
        zonesToStop.map(z =>
          Connector.stop(z.zoneId).catch(err =>
            console.error(`[Multizone] stop on exit failed for ${z.zoneName}:`, err)
          )
        )
      );
    }

    setMultizoneState({
      enabled: false,
      masterZoneId: null,
      masterVolume: 50,
      groupedZones: [],
    });

    // Resume single-zone playback on primary if something was playing
    if (wasPlaying && store.currentTrack) {
      // QueueController uses getFormatIdForRenderer (no longer multizone)
      const queueIdx = store.queueIndex;
      await QueueController.playQueueTrack(queueIdx);
    }

    primaryZoneRef.current = null;
  }, [useNative, setMultizoneState]);

  // --- Zone selection (toggle) ---

  const toggleZoneInGroup = useCallback((zone) => {
    const state = useMaestroStore.getState().multizone;
    const existing = state.groupedZones.find((z) => z.zoneId === zone.id);
    console.log('[toggleZoneInGroup]', existing ? 'REMOVING' : 'ADDING', zone.name, '(' + zone.id + ')',
      'current zones:', state.groupedZones.length, 'enabled:', state.enabled);

    if (existing) {
      const updated = state.groupedZones.filter((z) => z.zoneId !== zone.id);
      if (useNative) {
        getClientMultizoneHandler().removeFromGroup(zone.id);
      } else {
        const masterGone = state.masterZoneId === zone.id;
        const newMaster = masterGone && updated.length > 0 ? updated[0].zoneId : state.masterZoneId;
        const avg = updated.length > 0
          ? Math.round(updated.reduce((sum, z) => sum + z.actualVolume, 0) / updated.length)
          : 50;
        setMultizoneState({
          ...state,
          groupedZones: updated.map((z) =>
            z.zoneId === newMaster ? { ...z, isMaster: true } : { ...z, isMaster: false }
          ),
          masterZoneId: updated.length > 0 ? newMaster : null,
          masterVolume: avg,
          enabled: updated.length >= 2,
        });
      }
    } else {
      const newZone = {
        zoneId: zone.id,
        zoneName: zone.name,
        zoneType: zone.type || 'unknown',
        relativeVolume: 100,
        actualVolume: state.masterVolume,
        isMaster: state.groupedZones.length === 0,
      };
      if (useNative) {
        getClientMultizoneHandler().addToGroup(zone.id, 100);
      } else {
        const updated = [...state.groupedZones, newZone];
        const avg = Math.round(updated.reduce((sum, z) => sum + z.actualVolume, 0) / updated.length);
        setMultizoneState({
          ...state,
          groupedZones: updated,
          masterZoneId: state.masterZoneId || zone.id,
          masterVolume: avg,
          enabled: updated.length >= 2,
        });
      }
    }
  }, [useNative, setMultizoneState]);

  const isZoneSelected = useCallback((zoneId) => {
    return useMaestroStore.getState().multizone.groupedZones.some((z) => z.zoneId === zoneId);
  }, []);

  // --- Master volume (ratio-based proportional scaling) ---

  const setMasterVolume = useCallback((vol) => {
    const newMaster = Math.max(0, Math.min(100, vol));
    const state = useMaestroStore.getState().multizone;
    const oldMaster = state.masterVolume;

    if (useNative) {
      getClientMultizoneHandler().setMasterVolume(newMaster);
      return;
    }

    let updatedZones;
    if (oldMaster === 0) {
      updatedZones = state.groupedZones.map((z) => ({
        ...z, actualVolume: newMaster,
      }));
    } else {
      const ratio = newMaster / oldMaster;
      updatedZones = state.groupedZones.map((z) => ({
        ...z, actualVolume: Math.min(100, Math.max(0, Math.round(z.actualVolume * ratio))),
      }));
    }

    setMultizoneState({
      ...state,
      masterVolume: newMaster,
      groupedZones: updatedZones,
    });

    if (isConnectorAvailable()) {
      clearTimeout(masterDebounceRef.current);
      masterDebounceRef.current = setTimeout(() => {
        const latest = useMaestroStore.getState().multizone;
        Promise.all(
          latest.groupedZones.map((z) =>
            Connector.setVolume(z.zoneId, z.actualVolume).catch((err) =>
              console.error(`[Multizone] setVolume failed for ${z.zoneName}:`, err)
            )
          )
        );
      }, VOLUME_DEBOUNCE_MS);
    }
  }, [useNative, setMultizoneState]);

  // --- Per-zone volume (direct, master = recalculated average) ---

  const setZoneVolume = useCallback((zoneId, vol) => {
    const clamped = Math.max(0, Math.min(100, vol));

    if (useNative) {
      getClientMultizoneHandler().setRelativeVolume(zoneId, clamped);
      return;
    }

    setGroupedZoneActualVolume(zoneId, clamped);

    if (isConnectorAvailable()) {
      clearTimeout(zoneDebounceRefs.current[zoneId]);
      zoneDebounceRefs.current[zoneId] = setTimeout(() => {
        Connector.setVolume(zoneId, clamped).catch((err) =>
          console.error(`[Multizone] setZoneVolume failed for ${zoneId}:`, err)
        );
      }, VOLUME_DEBOUNCE_MS);
    }
  }, [useNative, setGroupedZoneActualVolume]);

  // --- Transport: play on all zones ---

  const playOnAllZones = useCallback(async (params) => {
    const state = useMaestroStore.getState().multizone;

    if (useNative) {
      return getClientMultizoneHandler().playOnGroup({
        streamUrl: params.uri,
        title: params.title,
        artist: params.artist,
        album: params.album,
        artworkUrl: params.artUrl,
        duration: params.durationSecs,
        format: String(params.formatId),
      });
    }

    if (isConnectorAvailable()) {
      console.log('[Multizone] coordinated playOnAllZones:', state.groupedZones.length, 'zones');
      try {
        const results = await Connector.multizonePlay(
          state.groupedZones.map((z) => ({
            renderer_id: z.zoneId,
            volume: z.actualVolume ?? null,
          })),
          params.uri,
          params.title || '',
          params.artist || '',
          params.album || '',
          params.formatId || 27,
          params.durationSecs || 0,
          params.artUrl,
        );
        console.log('[Multizone] coordinated play results:', results);
        const anyOk = results.some((r) => r.success);
        return { success: anyOk };
      } catch (err) {
        console.error('[Multizone] coordinated play failed, falling back to individual:', err);
        const results = await Promise.allSettled(
          state.groupedZones.map((z) =>
            Connector.playUri(
              z.zoneId,
              params.uri,
              params.title || '',
              params.artist || '',
              params.album || '',
              params.formatId || 27,
              params.durationSecs || 0,
              params.artUrl,
            )
          )
        );
        const anyOk = results.some((r) => r.status === 'fulfilled');
        return { success: anyOk };
      }
    }

    return { success: false, error: 'No bridge available for multizone' };
  }, [useNative]);

  // --- Transport: resume all zones (Play action, NOT play_uri — preserves position) ---

  const resumeAllZones = useCallback(async () => {
    const state = useMaestroStore.getState().multizone;
    console.log('[Multizone] resumeAllZones:', state.groupedZones.length, 'zones');

    if (useNative) {
      return getClientMultizoneHandler().resumeOnGroup();
    }

    if (isConnectorAvailable()) {
      const results = await Promise.allSettled(
        state.groupedZones.map((z) =>
          Connector.play(z.zoneId).catch((err) => {
            console.error(`[Multizone] resume failed for ${z.zoneName}:`, err);
            throw err;
          })
        )
      );
      console.log('[Multizone] resumeAllZones results:', results);
      return { success: results.some((r) => r.status === 'fulfilled') };
    }

    return { success: false, error: 'No bridge available for multizone resume' };
  }, [useNative]);

  // --- Transport: pause all zones ---

  const pauseAllZones = useCallback(async () => {
    const state = useMaestroStore.getState().multizone;
    console.log('[Multizone] pauseAllZones:', state.groupedZones.length, 'zones');

    if (useNative) {
      return getClientMultizoneHandler().pauseOnGroup();
    }

    if (isConnectorAvailable()) {
      const results = await Promise.allSettled(
        state.groupedZones.map((z) =>
          Connector.pause(z.zoneId).catch((err) => {
            console.error(`[Multizone] pause failed for ${z.zoneName}:`, err);
            throw err;
          })
        )
      );
      console.log('[Multizone] pauseAllZones results:', results);
      return { success: results.some((r) => r.status === 'fulfilled') };
    }
  }, [useNative]);

  // --- Transport: seek all zones ---

  const seekAllZones = useCallback(async (positionSecs) => {
    const state = useMaestroStore.getState().multizone;

    if (useNative) {
      return getClientMultizoneHandler().seekOnGroup(positionSecs * 1000);
    }

    if (isConnectorAvailable()) {
      for (const z of state.groupedZones) {
        try {
          await Connector.seek(z.zoneId, positionSecs);
        } catch (err) {
          console.error(`[Multizone] seek failed for ${z.zoneName}:`, err);
        }
      }
    }
  }, [useNative]);

  // --- Volume sync from Connector (external knob / poll updates) ---

  useEffect(() => {
    if (!multizone.enabled || !isConnectorAvailable() || useNative) return;

    let unlisten;

    Connector.onVolumeChanged(({ rendererId, volume }) => {
      const mzState = useMaestroStore.getState().multizone;
      if (!mzState.enabled) return;
      const zone = mzState.groupedZones.find((z) => z.zoneId === rendererId);
      if (!zone) return;

      useMaestroStore.getState().setGroupedZoneActualVolume(rendererId, volume);
    }).then((fn) => { unlisten = fn; });

    return () => { unlisten?.(); };
  }, [multizone.enabled, useNative]);

  return {
    multizoneEnabled: multizone.enabled,
    masterVolume: multizone.masterVolume,
    groupedZones: multizone.groupedZones,
    masterZoneId: multizone.masterZoneId,

    enableMultizone,
    disableMultizone,
    toggleZoneInGroup,
    isZoneSelected,
    setMasterVolume,
    setZoneVolume,
    playOnAllZones,
    resumeAllZones,
    pauseAllZones,
    seekAllZones,
  };
}
